import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Combobox } from "../../../components/ui-kit/fields/combobox";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { RequiredAsterisk } from "../../../components/ui-kit/required-asterisk";
import {
  ppgConditionLabels,
  ppgMeasModes,
  ppgQuality,
} from "../../../constants/enum-to-text";
import { AddPPG } from "./add-ppg";

export const AddPPGForm = observer(function AddPPGForm(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const ppgConditionOptions = Object.entries(ppgConditionLabels).map(
    ([value, label]) => ({ value, label: resolveEnumLabel(label) }),
  );
  const ppgQualityOptions = Object.entries(ppgQuality).map(
    ([value, label]) => ({ value, label: resolveEnumLabel(label) }),
  );
  const [model] = useState(() => {
    const m = new AddPPG(patient.api, patient);
    void m.initEmpty();
    return m;
  });

  return (
    <div>
      <hr />
      <div>
        <h3 className="mt-9 mb-9 text-base font-bold">{model.desc}</h3>
        <Trans>
          (<RequiredAsterisk /> = mandatory)
        </Trans>
        <Form onSubmit={() => model.post()}>
          <FormTable dataAdd>
            <FormRow label={t`Patient HF study ID`} data={patient.patientId} />

            <FormRow
              label={
                <>
                  <Trans>Measured at</Trans> <RequiredAsterisk />
                </>
              }
              data={
                <DateInput
                  attr="measurement_datetime"
                  model={model.addingList}
                  type="datetime-local"
                />
              }
            />

            <FormRow
              label={
                <>
                  <Trans>Measurement condition</Trans> <RequiredAsterisk />
                </>
              }
              data={
                <Combobox
                  attr="measurement_condition"
                  model={model.addingList}
                  options={ppgConditionOptions}
                  placeholder={t`Select condition`}
                />
              }
            />

            <FormRow
              label={
                <>
                  <Trans>Measurement device</Trans> <RequiredAsterisk />
                </>
              }
              data={
                <TextInput attr="measurement_device" model={model.addingList} />
              }
            />

            <FormRow
              label={
                <>
                  <Trans>Measurement origin</Trans> <RequiredAsterisk />
                </>
              }
              data={
                <TextInput attr="measurement_origin" model={model.addingList} />
              }
            />

            <FormRow
              label={t`Measurement mode`}
              data={
                <Select
                  attr="measurement_mode"
                  model={model.addingList}
                  values={ppgMeasModes}
                />
              }
            />

            <FormRow
              label={t`Quality flag`}
              data={
                <Combobox
                  attr="quality_flag"
                  model={model.addingList}
                  options={ppgQualityOptions}
                />
              }
            />

            <FormRow
              label={t`Comment`}
              data={<TextInput attr="comment" model={model.addingList} />}
            />

            <FormRow
              label={
                <>
                  <Trans>PPG file (.csv)</Trans> <RequiredAsterisk />
                </>
              }
              data={
                <input
                  type="file"
                  onChange={(event) => {
                    model.uploadFile(event);
                  }}
                  accept="text/csv"
                />
              }
            />
          </FormTable>
          <br className="h-10" />
          <Button type="submit">
            <Trans>Post</Trans>
          </Button>
        </Form>
      </div>
      <hr />
    </div>
  );
});
